import type { AxiosInstance } from 'axios';

/** `/api/files/{id}/content` 형태의 저장 파일 URL 여부. */
export function isStoredFileContentUrl(src: string): boolean {
  return /\/files\/[^/?#]+\/content(?:[?#].*)?$/.test(src);
}

/** axios(Bearer)로 파일을 받아 blob URL 생성. `<img src>` 대신 NodeView에서 사용. */
export async function fetchAuthenticatedFileBlobUrl(
  client: AxiosInstance,
  src: string,
): Promise<string> {
  const path = toApiRelativePath(src);
  const { data } = await client.get<Blob>(path, { responseType: 'blob' });
  return URL.createObjectURL(data);
}

/**
 * v-html 등으로 렌더된 `<img src="/api/files/.../content">` 는 Authorization 없이
 * 요청되어 401이 난다. 컨테이너 내부 img를 axios blob URL로 치환한다.
 *
 * @returns revoke — 언마운트 시 blob URL 해제용
 */
export async function hydrateAuthenticatedStoredFileImages(
  root: HTMLElement,
  client: AxiosInstance,
): Promise<() => void> {
  const blobUrls: string[] = [];
  const imgs = root.querySelectorAll('img[src]');

  await Promise.all(
    Array.from(imgs).map(async (element) => {
      if (!(element instanceof HTMLImageElement)) {
        return;
      }
      const src = element.getAttribute('src');
      if (!src || !isStoredFileContentUrl(src)) {
        return;
      }
      try {
        const blobUrl = await fetchAuthenticatedFileBlobUrl(client, src);
        blobUrls.push(blobUrl);
        element.src = blobUrl;
      } catch {
        element.alt = element.alt || '이미지를 불러올 수 없습니다';
        element.classList.add('ax-stored-file-image--error');
      }
    }),
  );

  return () => {
    for (const url of blobUrls) {
      URL.revokeObjectURL(url);
    }
  };
}

/** 인증 헤더를 포함해 파일을 다운로드. */
export async function downloadAuthenticatedFile(
  client: AxiosInstance,
  src: string,
  filename: string,
): Promise<void> {
  const path = toApiRelativePath(src);
  const { data } = await client.get<Blob>(path, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename || 'download';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

function toApiRelativePath(src: string): string {
  if (src.startsWith('/api/')) {
    return src.slice('/api'.length);
  }
  return src.startsWith('/') ? src : `/${src}`;
}

export interface StoredFileUploadResult {
  id: string;
  url: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}

/** 리치 텍스트 에디터용 이미지 업로드. */
export async function uploadRichTextImage(
  client: AxiosInstance,
  file: File,
): Promise<StoredFileUploadResult> {
  const form = new FormData();
  form.append('originalName', file.name);
  form.append('file', file);
  const { data } = await client.post<StoredFileUploadResult>('/files', form);
  return data;
}
